import deepEqual from "fast-deep-equal";
import { atom } from "jotai";
import { atomFamily } from "jotai/utils";
import { getInterfaceAppDomain, getInterfacesOrigin } from "../utils/urls";

import { interfaceProjectAtomFamily_SUSPENDS } from "./interfaceProjectAtomFamily_SUSPENDS";
import { interfacePageAtomFamily_SUSPENDS } from "./interfacePageAtomFamily_SUSPENDS";

type InterfaceUrlParams = {
  projectId: string | undefined;
  pageId: string | undefined;
  nodeId: string;
  isFocusModeEnabled: boolean;
  proposalId?: string | undefined;
};

type InterfaceUrlResult = {
  url: string | null;
  projectName: string | null;
};

/**
 * Derives interface URLs and project data from existing atoms to avoid duplicate network calls.
 *
 * This atom family computes both the URL and project information for interface pages by
 * leveraging the already-cached interfaceProjectAtomFamily_SUSPENDS and
 * interfacePageAtomFamily_SUSPENDS atoms.
 *
 * Returns an object containing:
 * - url: The final URL for focus mode (considering custom domains, slugs, etc.)
 * - projectName: The interface project name for display purposes
 *
 * This eliminates the need for separate atom calls in components that need both URL and title.
 */
export const interfaceUrlAndNameAtomFamily_SUSPENDS = atomFamily(
  ({
    projectId,
    pageId,
    nodeId,
    isFocusModeEnabled,
    proposalId,
  }: InterfaceUrlParams) => {
    const urlAndNameAtom = atom(async (get): Promise<InterfaceUrlResult> => {
      if (!projectId || !pageId) {
        return { url: null, projectName: null };
      }

      try {
        // Get project and page data from existing atoms (leverages their caching)
        const [project, page] = await Promise.all([
          get(
            interfaceProjectAtomFamily_SUSPENDS({
              interfaceProjectId: projectId,
              proposalId,
              nodeId,
            }),
          ),
          get(
            interfacePageAtomFamily_SUSPENDS({
              interfaceProjectId: projectId,
              interfacePageId: pageId,
              nodeId,
              proposalId,
            }),
          ),
        ]);

        const projectSlug = project?.slug;
        const pageSlug = page?.slug;
        const customDomain = project?.customDomain;
        const projectName = project?.name || null;

        if (!projectSlug || !pageSlug || !pageId) {
          return { url: null, projectName };
        }

        if (isFocusModeEnabled) {
          return {
            url: `https://${getInterfacesOrigin()}/interfaces/page/${pageId}`,
            projectName,
          };
        }

        const url = customDomain
          ? `https://${customDomain}/${pageSlug}`
          : `https://${projectSlug}.${getInterfaceAppDomain()}/${pageSlug}`;

        return { url, projectName };
      } catch (error) {
        console.error("Error constructing interface URL:", error);
        return { url: null, projectName: null };
      }
    });

    urlAndNameAtom.debugLabel = `interfaceUrlAndNameAtomFamily_SUSPENDS(projectId:${projectId}, pageId:${pageId}, nodeId:${nodeId})`;
    return urlAndNameAtom;
  },
  deepEqual,
);
